import { useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import GenericWizard from 'components/GenericWizard';
import BasicModal from 'components/Modals/presets/BasicModal';
import ActivityService from 'services/ActivityService';
import { FIELD_TYPES } from 'utility/constants';
import { useDirtyState } from 'components/Builders/CustomFieldsBuilder/hooks/useDirtyState';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { getOriginalError } from 'services/AxiosService';
import { toastVariant, useToast } from 'components/ToastProvider';
import { getErrorMessage } from 'hooks/useErrors';

const stripNullKeys = (object) => {
  Object.keys(object).forEach(
    (key) => object[key] == null && delete object[key]
  );
};

const EMPTY_OBJECT = {};

export default function ActivityFieldWizard({
  show = false,
  onHide,
  context = EMPTY_OBJECT,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [wizardData, setWizardData] = useState({});
  const [canBeSaved, setCanBeSaved] = useState(false);
  const [errors, setErrors] = useState(EMPTY_OBJECT);
  const [showToast] = useToast();
  const handleSave = async () => {
    const preSend = cloneDeep(wizardData);
    try {
      const {
        displayName,
        isRequired,
        type,
        options,
        phonenumberOptions,
        moneyOptions,
        relation,
        rating,
        meta,
        name,
        description,
        descriptionVisibility,
      } = preSend;

      let payload = {
        activityObjectId: context.activityObjectId,
        displayName,
        description,
        descriptionVisibility,
        isRequired: isRequired || false,
        fieldType: type,
        ...(meta !== undefined && {
          meta,
        }),
        ...(options && options.length && { options }),
        ...(type === FIELD_TYPES.PhoneNumber.type && { phonenumberOptions }),
        ...(type === FIELD_TYPES.Rating.type && { rating }),
        ...(type === FIELD_TYPES.Money.type && { moneyOptions }),
        ...(type === FIELD_TYPES.Relationship.type && {
          relation: {
            ...relation,
            selectedEntities: undefined,
            selectedEntityIds: (relation?.selectedEntities || []).map(
              (e) => e.id
            ),
          },
        }),
      };

      if (context.field) {
        // patch,
        const { id, meta: oldMeta } = context.field;
        payload = {
          ...payload,
          meta: {
            ...oldMeta,
            ...meta,
          },
          id,
          name,
        };
        stripNullKeys(payload);

        payload = await ActivityService.v2UpdateField(payload);
      } else {
        // create
        payload = {
          ...payload,
          isDefault: false,
          isReadOnly: false,
          isHidden: false,
          meta: { ...meta, cols: 1 }, // default to 1 column
        };
        stripNullKeys(payload);
        payload = await ActivityService.v2CreateField(payload);
      }
      onConfirm(payload);
    } catch (error) {
      const orig = getOriginalError(error);

      showToast({
        message:
          getErrorMessage(orig) ||
          t('Failed to save the field. Please try again.'),
        variant: toastVariant.FAILURE,
      });

      if (orig) {
        setErrors({
          name: orig?.name,
          displayName: orig?.display_name,
          description: orig?.description,
        });
      }
    }
  };
  const initialDataRef = useRef(null);

  const onChange = ({ data, isComplete }) => {
    if (!initialDataRef.current) {
      initialDataRef.current = data;
    }
    setWizardData(data);
    setCanBeSaved(isComplete);
    setErrors(EMPTY_OBJECT);
  };

  const { field } = context;

  const { showConfirmation, onCheckToHide, onHideConfirmed, onHideCanceled } =
    useDirtyState(initialDataRef.current, wizardData, onHide);

  return (
    <>
      <BasicModal
        disabled={!canBeSaved}
        fitContent={false}
        heading={field ? t('Edit Field') : t('Add New Field')}
        onConfirm={handleSave}
        onHide={onCheckToHide}
        show={show}
        size="medium"
        typeOfContent="wizard"
      >
        <GenericWizard
          wizard="ActivityFieldWizard"
          onChange={onChange}
          data={context}
          errors={errors}
        />
      </BasicModal>
      <ConfirmationModal
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        defaultLeftBtnText={t('Cancel')}
        actionBtnColor="red"
        show={showConfirmation}
        onConfirm={onHideConfirmed}
        onHide={onHideCanceled}
      >
        {t('Unsaved changes will be lost, would you like to continue?')}
      </ConfirmationModal>
    </>
  );
}
